import { CheckCircle, MessageSquare, AlertTriangle, UserCircle, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import type { DashboardActivityItem } from '@uniticket/shared';

const activityIcons: Record<string, { icon: typeof CheckCircle; bg: string; color: string }> = {
  resolved: { icon: CheckCircle, bg: 'bg-green-50', color: 'text-green-600' },
  'added comment': { icon: MessageSquare, bg: 'bg-blue-50', color: 'text-blue-600' },
  commented: { icon: MessageSquare, bg: 'bg-blue-50', color: 'text-blue-600' },
  escalated: { icon: AlertTriangle, bg: 'bg-red-50', color: 'text-red-600' },
  assigned: { icon: UserCircle, bg: 'bg-purple-50', color: 'text-purple-600' },
  reassigned: { icon: UserCircle, bg: 'bg-purple-50', color: 'text-purple-600' },
  created: { icon: PlusCircle, bg: 'bg-brand-rose/10', color: 'text-brand-rose' },
  'changed status': { icon: CheckCircle, bg: 'bg-green-50', color: 'text-green-600' },
  'added internal note': { icon: MessageSquare, bg: 'bg-amber-50', color: 'text-amber-600' },
};

function getIconConfig(action: string) {
  for (const [key, config] of Object.entries(activityIcons)) {
    if (action.toLowerCase().includes(key)) return config;
  }
  return { icon: CheckCircle, bg: 'bg-slate-50', color: 'text-slate-600' };
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return '1 day ago';
  return `${diffD} days ago`;
}

interface ActivityFeedProps {
  activities?: DashboardActivityItem[];
  loading?: boolean;
}

export function ActivityFeed({ activities, loading }: ActivityFeedProps) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="gradient-band px-6 py-3 flex items-center justify-between">
        <h3 className="text-sm font-bold text-white uppercase tracking-wide">{t('activityFeed.title')}</h3>
        <span className="text-xs text-white/60">{t('activityFeed.subtitle')}</span>
      </div>

      {loading ? (
        <div className="divide-y divide-slate-50">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="px-6 py-3.5 flex items-start gap-3">
              <Skeleton className="w-8 h-8 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : !activities || activities.length === 0 ? (
        <div className="px-6 py-12 text-center text-slate-400 text-sm">
          {t('activityFeed.noActivity')}
        </div>
      ) : (
        <div className="divide-y divide-slate-50">
          {activities.map((activity) => {
            const { icon: Icon, bg, color } = getIconConfig(activity.action);
            return (
              <div key={activity.id} className="px-6 py-3.5 flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">{activity.userName}</span>{' '}
                    {activity.action} on{' '}
                    <Link to={`/tickets/${activity.ticketId}`} className="font-semibold text-brand-rose hover:underline">
                      {activity.ticketNumber}
                    </Link>
                  </p>
                  {activity.detail && (
                    <p className="text-xs text-slate-500 mt-0.5 italic truncate">{activity.detail}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-0.5">{formatRelativeTime(activity.createdAt)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
